import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// PostHog client instances
let client: any = null; // Client-side
let serverClient: any = null; // Server-side

// Initialize PostHog server client for SSR
if (typeof window === 'undefined') {
  // Server-side PostHog client
  import('posthog-node')
    .then((posthog: any) => {
      // Handle different module formats
      const PostHogClass = posthog.PostHog || posthog.default?.PostHog || posthog.default;

      if (!PostHogClass) {
        throw new Error('PostHog class not found in module');
      }

      const posthogKey =
        process.env.NEXT_PUBLIC_POSTHOG_KEY || 'phc_r7aKGiVnq4AK2VkGGOqtBLpIT9RSIA6dcPHblKfqTar';
      const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

      serverClient = new PostHogClass(posthogKey, {
        host: posthogHost,
      });
      console.log('PostHog Debug - Server client initialized');
    })
    .catch(error => {
      console.error('PostHog Debug - Failed to import PostHog Node SDK:', error);
      console.error('PostHog Debug - Error details:', error.message);
      console.error('PostHog Debug - Error stack:', error.stack);
    });
}

// Initialize PostHog client-side (for event tracking only)
if (typeof window !== 'undefined') {
  import('posthog-js')
    .then((posthog: any) => {
      const posthogKey =
        process.env.NEXT_PUBLIC_POSTHOG_KEY || 'phc_r7aKGiVnq4AK2VkGGOqtBLpIT9RSIA6dcPHblKfqTar';
      const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

      client = posthog.default.init(posthogKey, {
        api_host: posthogHost,
        loaded: (ph: any) => {
          if (process.env.NODE_ENV === 'development') ph.debug();
        },
      });
    })
    .catch(error => {
      console.error('PostHog Debug - Failed to import PostHog:', error);
    });
}

// Server-side function to fetch feature flags
export async function getServerSideFlags(
  distinctId?: string,
  userProperties?: Record<string, any>
) {
  if (typeof window !== 'undefined') {
    console.log('PostHog Debug - getServerSideFlags called on client side, skipping');
    return {};
  }

  try {
    // Wait for server client to be initialized
    let attempts = 0;
    const maxAttempts = 20;

    while (!serverClient && attempts < maxAttempts) {
      console.log(
        `PostHog Debug - Server client not initialized, waiting... (attempt ${
          attempts + 1
        }/${maxAttempts})`
      );
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (!serverClient) {
      console.log('PostHog Debug - Server client not available');
      return {};
    }

    // Fetch flags for the given distinct ID (or anonymous)
    const flags = await serverClient.getAllFlags(distinctId || 'anonymous', userProperties);
    console.log('PostHog Debug - Server-side flags:', flags);

    return flags;
  } catch (error) {
    console.error('PostHog Debug - Failed to fetch server-side flags:', error);
    return {};
  }
}

// Remote configuration context interface
interface RemoteConfigContextType {
  config: Record<string, any>;
  isLoading: boolean;
  error: string | null;
  // eslint-disable-next-line no-unused-vars
  capture: (event: string, properties?: Record<string, any>) => void;
  // eslint-disable-next-line no-unused-vars
  getRemoteConfig: (configKey: string) => any;
}

// Create context
const RemoteConfigContext = createContext<RemoteConfigContextType | undefined>(undefined);

// Provider props interface
interface RemoteConfigProviderProps {
  children: ReactNode;
  serverFlags?: Record<string, any>; // Pass server-side flags as props
  distinctId?: string; // User distinct ID for client-side hydration
}

// Remote configuration provider component
export function RemoteConfigProvider({ children, serverFlags = {} }: RemoteConfigProviderProps) {
  const [config] = useState<Record<string, any>>(serverFlags);
  const [isLoading, setIsLoading] = useState(!serverFlags || Object.keys(serverFlags).length === 0);
  const [error] = useState<string | null>(null);

  useEffect(() => {
    // If we have server flags, we're done loading
    if (serverFlags && Object.keys(serverFlags).length > 0) {
      console.log('PostHog Debug - Using server-side flags, skipping client-side fetch');
      setIsLoading(false);
      return;
    }

    // Only run client-side logic if no server flags provided
    if (typeof window === 'undefined') {
      console.log('PostHog Debug - Server side, skipping client-side logic');
      return;
    }

    console.log('PostHog Debug - No server flags provided, using empty config');
    setIsLoading(false);
  }, [serverFlags]); // Re-run if serverFlags change

  const capture = (event: string, properties?: Record<string, any>) => {
    try {
      if (client) {
        client.capture(event, properties);
      }
    } catch (err) {
      console.error('Failed to capture event:', err);
    }
  };

  const getRemoteConfig = (configKey: string): any => {
    try {
      // Check our local config (from server)
      if (config[configKey] !== undefined) {
        return config[configKey];
      }

      return null;
    } catch (err) {
      console.error(`Failed to get remote config ${configKey}:`, err);
      return null;
    }
  };

  const value: RemoteConfigContextType = {
    config,
    isLoading,
    error,
    capture,
    getRemoteConfig,
  };

  return <RemoteConfigContext.Provider value={value}>{children}</RemoteConfigContext.Provider>;
}

// Hook to use remote configuration
export function useRemoteConfig() {
  const context = useContext(RemoteConfigContext);
  if (context === undefined) {
    console.warn('useRemoteConfig: Provider not available, returning default context');
    return {
      config: {},
      isLoading: true,
      error: null,
      capture: () => {
        console.warn('useRemoteConfig: capture called but provider not available');
      },
      getRemoteConfig: () => {
        console.warn('useRemoteConfig: getRemoteConfig called but provider not available');
        return null;
      },
    };
  }

  return context;
}

// Hook to get a specific remote configuration value
export function useConfigValue<T = any>(key: string, defaultValue?: T): T | undefined {
  const { config, isLoading } = useRemoteConfig();

  if (isLoading) {
    return defaultValue;
  }

  return config[key] !== undefined ? config[key] : defaultValue;
}

// Hook to get remote configuration value directly from PostHog
export function useRemoteConfigValue(configKey: string): any {
  const context = useContext(RemoteConfigContext);
  if (context === undefined) {
    console.warn(`useRemoteConfigValue: Provider not available for ${configKey}`);
    return null;
  }

  return context.config[configKey];
}

// Component to conditionally render based on remote configuration value
interface ConfigConditionProps {
  key: string;
  children: ReactNode;
  fallback?: ReactNode;
  condition?: (value: any) => boolean;
}

export function ConfigCondition({
  key,
  children,
  fallback = null,
  condition = value => Boolean(value),
}: ConfigConditionProps) {
  const value = useConfigValue(key);

  if (condition(value)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// Component to conditionally render based on remote configuration
interface RemoteConfigProps {
  configKey: string;
  children: ReactNode;
  fallback?: ReactNode;
  condition?: (value: any) => boolean;
}

export function RemoteConfig({
  configKey,
  children,
  fallback = null,
  condition = value => Boolean(value),
}: RemoteConfigProps) {
  const configValue = useRemoteConfigValue(configKey);

  if (condition(configValue)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// Export the PostHog clients for direct usage if needed
export { client as posthogClient, serverClient as posthogServerClient };

// Legacy exports for backward compatibility
export const FeatureFlagsProvider = RemoteConfigProvider;
export const useFeatureFlags = useRemoteConfig;
